/**
 * The world-to-scope mapping for the radar, kept pure so it can be tested.
 *
 * **This exists because getting it wrong is invisible in a screenshot.** The
 * first version drew the view wedge at `-PI/2 - turretYaw` instead of
 * `-PI/2 + turretYaw`: the scope looked entirely plausible — a wedge, pointing
 * somewhere, moving when the player moved the mouse — and was mirrored. It was
 * reported as *"I faced north and moved the camera left, and the wedge went
 * right"*, and it took a player noticing rather than any check here.
 *
 * Everything below follows from one fact, which is the simulation's and not this
 * file's to choose. `stepTank` and `spawnShell` advance by
 *
 *   x += sin(yaw)          z += cos(yaw)
 *
 * so **yaw 0 faces +Z and yaw +90° faces +X**. The scope draws +Z up and +X
 * right, which makes a heading of `yaw` sit exactly `yaw` **clockwise from up**
 * — and screen space is y-down, so clockwise is the positive direction there
 * too. Both helpers are that sentence, written twice.
 */

/**
 * Canvas angle for a world heading, in radians.
 *
 * Canvas measures from +X (right) and increases clockwise on screen, so "up" is
 * `-PI/2` and a heading adds to it. Feed this to `ctx.arc`.
 */
export function scopeAngleFor(yaw: number): number {
  return -Math.PI / 2 + yaw;
}

/**
 * Where a point `dx` east and `dz` north of the player belongs on the scope, in
 * pixels from its centre, before any range clamp.
 *
 * `y` is negated once and only here: world Z grows north and screen Y grows
 * down. A second negation anywhere downstream mirrors the display, which is the
 * defect this module is named after.
 */
export function scopeOffset(dx: number, dz: number, metresToPixels: number): { x: number; y: number } {
  return { x: dx * metresToPixels, y: -dz * metresToPixels };
}

/**
 * Where a **world point** belongs on the scope, in pixels from its centre.
 *
 * The same mapping as `scopeOffset`, with the subtraction that turns a place on
 * the map into a place relative to the player folded in — which is the step the
 * boundary rectangle got wrong for months by writing its own. Mission geometry
 * is all world points (a route waypoint, a spawn gate, the core's post), so
 * every one of them composes through here and can never pick up a second
 * negation on the way.
 */
export function scopeOffsetTo(
  point: { x: number; z: number },
  origin: { x: number; z: number },
  metresToPixels: number,
): { x: number; y: number } {
  return scopeOffset(point.x - origin.x, point.z - origin.z, metresToPixels);
}

/**
 * An offset pulled back onto the scope, with a flag saying whether it had to
 * be.
 *
 * Contacts already do this by hand so that a tank beyond the rim keeps its
 * *bearing* while its distance stops meaning anything. Mission markers want the
 * identical treatment for a stronger reason: the core, the convoy and the
 * terminus are frequently the far side of the map, and a marker silently
 * dropped at the rim is the one piece of information the strip cannot replace.
 *
 * The direction is preserved exactly — this scales the vector rather than
 * clamping its components, which would drag a marker round the rim toward a
 * corner and report a bearing nothing is on.
 */
export function clampToScope(
  offset: { x: number; y: number },
  limit: number,
): { x: number; y: number; clamped: boolean } {
  const dist = Math.hypot(offset.x, offset.y);
  if (dist <= limit || dist === 0) return { x: offset.x, y: offset.y, clamped: false };
  return { x: (offset.x / dist) * limit, y: (offset.y / dist) * limit, clamped: true };
}
